import React, { useCallback, ComponentType, SVGProps } from 'react'
import { SimpleLegend } from './components/SimpleLegend'
import { SingleDonutChart } from './components/SingleDonutChart'
import { useChartVisibility } from './hooks/useChartVisibility'
import Stack from '@mui/material/Stack'
import { Box } from '@mui/material'
import { DonutChartData, DonutLegendItem } from './types'

interface UnifiedDonutChartsProps<T> {
    data: DonutChartData<T>[]
    legend: DonutLegendItem[]
    size?: number
    firstChartSize?: number
    onTotalClick?: (visibleData: DonutChartData<T>) => void
    onTitleClick?: (visibleData: DonutChartData<T>) => void
    firstChartOnTitleClick?: (visibleData: DonutChartData<T>) => void
}

export const UnifiedDonutCharts = <T,>({
    data,
    legend,
    onTotalClick,
    onTitleClick,
    firstChartOnTitleClick,
    size = 180,
    firstChartSize,
}: UnifiedDonutChartsProps<T>) => {
    const {
        hiddenItems,
        hoveredIndex,
        pendingHoverIndex,
        activeHoverIndex,
        registerChart,
        toggleItem,
        focusItem,
        setHover,
    } = useChartVisibility(legend)

    const handleRegisterChart = useCallback(
        (chartIndex: number) => (chart: any) => {
            registerChart(chartIndex, chart)
        },
        [registerChart]
    )

    return (
        <Stack direction="column" gap={2} flex={1}>
            <SimpleLegend
                items={legend}
                hiddenItems={hiddenItems}
                hoveredIndex={hoveredIndex}
                pendingIndex={pendingHoverIndex}
                activeIndex={activeHoverIndex}
                onItemClick={toggleItem}
                onItemHover={setHover}
                direction="row"
            />

            <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(180px, 1fr))" gap={2} width="100%">
                {data.map((chart, index) => (
                    <SingleDonutChart<T>
                        key={chart.title}
                        data={chart.values}
                        legend={legend}
                        hiddenItems={hiddenItems}
                        title={chart.title}
                        size={index === 0 && firstChartSize ? firstChartSize : size}
                        disableTotalClick={chart.disableTotalClick}
                        onRegister={handleRegisterChart(index)}
                        onSliceClick={focusItem}
                        onTotalClick={onTotalClick}
                        onTitleClick={index === 0 && firstChartOnTitleClick ? firstChartOnTitleClick : onTitleClick}
                    />
                ))}
            </Box>
        </Stack>
    )
}
